import { useState } from "react";
import { Send, CheckCircle, Globe, Mail, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { motion } from "framer-motion";

const interests = [
  "Higher Education Programs",
  "Executive Training",
  "Professional Development",
  "Institutional Partnerships",
  "Research Collaboration",
  "Other",
];

const regions = [
  "North America",
  "Europe",
  "Asia Pacific",
  "Latin America",
  "Middle East",
  "Africa",
  "Other",
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country: "",
    interest: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Thank you! Our team will connect with you shortly.");
      setFormData({ name: "", email: "", country: "", interest: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-surface-solid relative overflow-hidden"
    >
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm uppercase tracking-widest font-semibold text-primary mb-4 block">
              Connect With Us
            </span>
            <h2
              className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-6"
              data-testid="contact-heading"
            >
              Start Your Global Journey Today
            </h2>
            <p className="text-lg text-white/60 leading-relaxed mb-10">
              Whether you're a student, professional, or institution, we're here 
              to help you navigate the world of international education and opportunity.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center border border-primary/20">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Global Headquarters</h4>
                  <p className="text-white/60">Houston, Texas, USA</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center border border-primary/20">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Email</h4>
                  <p className="text-white/60">info@m2l-usa.com</p>
                </div>
              </div>
            </div>

            {/* Tagline */}
            <div className="mt-12 pt-8 border-t border-white/10">
              <p className="text-lg font-light italic text-white/50">
                "Connecting Talent, Education, and Opportunity Across Borders."
              </p>
            </div>
          </motion.div>

          {/* Right Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-card rounded-2xl p-8 md:p-10"
          >
            {isSubmitted ? (
              <div
                className="flex flex-col items-center justify-center h-full py-12"
                data-testid="contact-success"
              >
                <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-primary" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-white mb-2">
                  Thank You!
                </h3>
                <p className="text-white/60 text-center max-w-sm">
                  Our global team will connect with you within 24 hours to discuss your journey.
                </p>
                <Button
                  data-testid="contact-reset"
                  className="mt-6 bg-transparent border border-primary text-primary hover:bg-primary/10"
                  onClick={() => setIsSubmitted(false)}
                >
                  Submit Another Inquiry
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <h3 className="font-heading text-2xl font-bold text-white mb-8">
                  Begin Your Journey
                </h3>

                <div className="space-y-5">
                  <div>
                    <Input
                      data-testid="contact-name"
                      placeholder="Full Name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-xl h-12 focus:border-primary focus:ring-primary/20"
                    />
                  </div>

                  <div>
                    <Input
                      data-testid="contact-email"
                      type="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      required
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-xl h-12 focus:border-primary focus:ring-primary/20"
                    />
                  </div>

                  <div>
                    <Select
                      value={formData.country}
                      onValueChange={(value) =>
                        setFormData({ ...formData, country: value })
                      }
                    >
                      <SelectTrigger data-testid="contact-country" className="bg-white/5 border-white/10 text-white rounded-xl h-12 focus:border-primary [&>span]:text-white/40 [&[data-state=open]>span]:text-white">
                        <SelectValue placeholder="Select Region" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-white/10">
                        {regions.map((region) => (
                          <SelectItem key={region} value={region} className="text-white hover:bg-primary/20 focus:bg-primary/20 focus:text-white">
                            {region}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Select
                      value={formData.interest}
                      onValueChange={(value) =>
                        setFormData({ ...formData, interest: value })
                      }
                    >
                      <SelectTrigger data-testid="contact-interest" className="bg-white/5 border-white/10 text-white rounded-xl h-12 focus:border-primary [&>span]:text-white/40 [&[data-state=open]>span]:text-white">
                        <SelectValue placeholder="Area of Interest" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-white/10">
                        {interests.map((interest) => (
                          <SelectItem key={interest} value={interest} className="text-white hover:bg-primary/20 focus:bg-primary/20 focus:text-white">
                            {interest}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Textarea
                      data-testid="contact-message"
                      placeholder="Tell us about your goals..."
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      rows={4}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-xl resize-none focus:border-primary focus:ring-primary/20"
                    />
                  </div>

                  <Button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={isSubmitting || !formData.name || !formData.email || !formData.country || !formData.interest}
                    className="w-full bg-primary text-[#060B19] hover:bg-primary-hover rounded-lg py-6 text-lg font-semibold transition-all disabled:opacity-50 group btn-glow"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Connect With Us
                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
